import { useEffect, useState } from "react";
import { useLocation } from "react-router-dom";
import {
  Menu, X, Droplets, Layers, FlaskConical, Microscope, Dna, Activity, Waves, Boxes,
  Phone, Mail, MessageCircle, MapPin, FlaskRound, FileSearch, Eye, ClipboardCheck, ChevronDown, Send,
} from "lucide-react";
import ImageWithFallback from "@/components/ImageWithFallback";
import { BRAND, IMAGES } from "@/lib/brand";
import { useSeo } from "@/lib/useSeo";

const NAV = [
  { label: "Home", href: "#home" },
  { label: "About", href: "#about" },
  { label: "Testing Services", href: "#services" },
  { label: "Why BIOSYN Aqua Lab", href: "#why" },
  { label: "Careers", href: "#careers" },
  { label: "Contact", href: "#contact" },
];

const SERVICES = [
  { title: "Water Quality Testing", icon: Droplets, items: "pH, Temperature, Salinity, Dissolved Oxygen, ORP, TAN, Ammonia, Nitrite, Nitrate, Alkalinity, Hardness, Calcium, Magnesium, Potassium and Total Nitrogen." },
  { title: "Soil & Pond Bottom Testing", icon: Layers, items: "Soil pH, Organic Matter, Organic Carbon, Nitrogen, Phosphorus, Potassium, Calcium, Magnesium, Sulphur, sediment condition and pond-bottom assessment." },
  { title: "Mineral & Nutrient Analysis", icon: FlaskConical, items: "Calcium, Magnesium, Potassium, Sodium, Chloride and other important mineral parameters." },
  { title: "Plankton Analysis", icon: Waves, items: "Phytoplankton identification, zooplankton identification, plankton density, dominant species and algal bloom risk assessment." },
  { title: "Microbiological Testing", icon: Microscope, items: "Total bacterial load, Vibrio screening, Vibrio enumeration and microbial status assessment." },
  { title: "Shrimp & Fish Health", icon: Activity, items: "Clinical examination, microscopic examination, gill examination, hepatopancreas assessment and parasite screening." },
  { title: "Pathogen Screening", icon: Dna, items: "EHP screening, WSSV screening, Vibrio screening and molecular/PCR-based pathogen detection." },
  { title: "Complete Pond Analysis", icon: Boxes, items: "Integrated Water + Soil + Minerals + Plankton + Microbiology + Shrimp/Fish Health + Pathogen Screening." },
];

const PROCESS = [
  { label: "TEST", icon: FlaskRound },
  { label: "ANALYZE", icon: FileSearch },
  { label: "MONITOR", icon: Eye },
  { label: "MANAGE", icon: ClipboardCheck },
];

const WHY = [
  { title: "Comprehensive Testing", desc: "Multiple aquaculture parameters under one laboratory service.", icon: Boxes },
  { title: "Advanced Diagnostics", desc: "Specialized testing for shrimp and fish health and important aquaculture pathogens.", icon: Microscope },
  { title: "Scientific Analysis", desc: "Laboratory-based analysis to support informed aquaculture management.", icon: FlaskConical },
  { title: "Complete Pond Profiling", desc: "Integrated testing of water, soil, minerals, plankton, microbiology, health and pathogens.", icon: Layers },
];

const LAB_PHONE = "+918985930908";
const LAB_PHONE_INTL = "918985930908";
const LAB_EMAIL = "sales@biosyn.com";
const WA_MESSAGE = "Hello BIOSYN Aqua Lab, I am interested in your aquaculture testing and diagnostic services. Please provide more information.";

const RESUME_EMAIL = "sales@bbiosyn.com";
const RESUME_MAILTO = `mailto:${RESUME_EMAIL}?subject=${encodeURIComponent("Career Application - BIOSYN")}&body=${encodeURIComponent(
`Dear BIOSYN Team,

I am interested in exploring career opportunities with BIOSYN.
Please find my resume attached for your consideration.

Name:
Phone:
Location:
Preferred Position:

Regards,
[Your Name]`)}`;

function scrollTo(id) {
  const el = document.getElementById(id);
  if (el) el.scrollIntoView({ behavior: "smooth" });
}

export default function AquaLab() {
  const [menuOpen, setMenuOpen] = useState(false);
  const location = useLocation();

  useEffect(() => {
    const hash = location.hash.slice(1);
    if (hash) setTimeout(() => document.getElementById(hash)?.scrollIntoView({ behavior: "smooth" }), 300);
  }, [location.hash]);

  useSeo({
    title: "BIOSYN Aqua Lab | Advanced Aquaculture Testing & Diagnostic Laboratory",
    description: "Biosyn Aqua Lab provides aquaculture testing and diagnostic services for shrimp and fish farming, covering water quality, soil, minerals, plankton, microbiology, pathogens and shrimp & fish health.",
    image: IMAGES.microscope,
  });

  return (
    <div style={{ background: "#F5FAFC", color: "#17324D" }}>
      {/* HEADER */}
      <header data-testid="lab-header" className="fixed top-0 left-0 right-0 z-50 border-b" style={{ background: "rgba(255,255,255,0.92)", backdropFilter: "blur(16px)", borderColor: "rgba(8,127,191,0.1)" }}>
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 flex items-center justify-between py-3">
          <button onClick={() => scrollTo("home")} className="flex items-center" data-testid="lab-logo">
            <img src={BRAND.logo} alt="BIOSYN Aqua Lab logo" className="h-10 md:h-12 w-auto" />
          </button>
          <nav className="hidden lg:flex items-center gap-1">
            {NAV.map((l) => (
              <button key={l.href} onClick={() => scrollTo(l.href.slice(1))} data-testid={`lab-nav-${l.label.toLowerCase().replace(/\s/g, "-")}`} className="px-3 py-2 text-sm font-semibold rounded-full transition-colors" style={{ color: "#17324D" }}>
                {l.label}
              </button>
            ))}
          </nav>
          <div className="hidden lg:block">
            <button onClick={() => scrollTo("contact")} data-testid="lab-get-tested-btn" className="btn-primary !px-5 !py-2.5 text-sm">Get Your Pond Tested</button>
          </div>
          <button onClick={() => setMenuOpen(!menuOpen)} data-testid="lab-menu-btn" aria-label="Menu" className="lg:hidden p-2.5 rounded-full border" style={{ borderColor: "rgba(8,127,191,0.25)", color: "#063B63" }}>
            {menuOpen ? <X className="w-5 h-5" /> : <Menu className="w-5 h-5" />}
          </button>
        </div>
        {menuOpen && (
          <div className="lg:hidden border-t bg-white" style={{ borderColor: "rgba(8,127,191,0.1)" }}>
            <div className="flex flex-col p-4 gap-1">
              {NAV.map((l) => (
                <button key={l.href} onClick={() => { setMenuOpen(false); scrollTo(l.href.slice(1)); }} className="px-4 py-3 rounded-xl text-left text-base font-semibold" style={{ color: "#17324D" }}>
                  {l.label}
                </button>
              ))}
              <button onClick={() => { setMenuOpen(false); scrollTo("contact"); }} className="btn-primary mt-2">Get Your Pond Tested</button>
            </div>
          </div>
        )}
      </header>

      {/* HERO */}
      <section id="home" className="relative overflow-hidden pt-[64px]" style={{ background: "#063B63" }}>
        <div className="absolute inset-0">
          <ImageWithFallback src={IMAGES.microscope} alt="Aquaculture diagnostic laboratory microscope analysis" className="w-full h-full object-cover" />
          <div className="absolute inset-0" style={{ background: "linear-gradient(100deg, rgba(6,59,99,0.95) 0%, rgba(6,59,99,0.85) 45%, rgba(8,127,191,0.55) 100%)" }} />
        </div>
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 relative py-24 md:py-36">
          <div className="max-w-3xl reveal">
            <p className="overline-label mb-3" style={{ color: "#16B8D4" }}>BIOSYN Aqua Lab</p>
            <h1 className="font-heading text-4xl sm:text-5xl lg:text-6xl font-extrabold tracking-tight leading-[1.05] text-white">
              Advanced Aquaculture Testing &amp; Diagnostic Laboratory
            </h1>
            <p className="mt-5 text-xl md:text-2xl font-semibold" style={{ color: "#16B8D4" }}>
              Precision Testing for Healthier Ponds, Stronger Shrimp &amp; Better Fish Production
            </p>
            <p className="mt-4 text-base md:text-lg leading-relaxed" style={{ color: "rgba(255,255,255,0.82)" }}>
              Biosyn Aqua Lab provides aquaculture testing and diagnostic services for shrimp and fish farming, covering water
              quality, soil and pond-bottom conditions, minerals, plankton, microbiology, pathogens and shrimp &amp; fish health.
            </p>
            <button onClick={() => scrollTo("contact")} data-testid="lab-hero-cta" className="btn-green mt-8">
              Get Your Pond Tested
            </button>
          </div>
        </div>
        <div className="absolute bottom-0 left-0 right-0 leading-[0]">
          <svg className="w-full h-[60px] md:h-[80px]" viewBox="0 0 1440 120" preserveAspectRatio="none">
            <path fill="#F5FAFC" d="M0,64L48,58.7C96,53,192,43,288,48C384,53,480,75,576,74.7C672,75,768,53,864,48C960,43,1056,53,1152,64C1248,75,1344,85,1392,90.7L1440,96L1440,120L0,120Z" />
          </svg>
        </div>
      </section>

      {/* ABOUT */}
      <section id="about" className="py-16 md:py-24">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 grid lg:grid-cols-2 gap-12 items-center">
          <div>
            <p className="overline-label mb-3">About BIOSYN Aqua Lab</p>
            <h2 className="font-heading text-3xl sm:text-4xl font-extrabold tracking-tight" style={{ color: "#063B63" }}>
              Laboratory Testing Built for Aquaculture
            </h2>
            <p className="mt-5 text-base leading-relaxed" style={{ color: "#4A6B82" }}>
              Biosyn Aqua Lab provides laboratory testing and diagnostic services designed specifically for shrimp and fish
              aquaculture. Our testing solutions help aquaculture professionals understand pond conditions, monitor important
              parameters, identify potential risks and make informed management decisions.
            </p>
            <div className="mt-7 grid grid-cols-2 gap-3">
              {["Accurate Testing", "Scientific Analysis", "Early Risk Monitoring", "Better Aquaculture Management"].map((f) => (
                <div key={f} className="biosyn-card px-4 py-3.5 flex items-center gap-2.5">
                  <span className="w-2 h-2 rounded-full shrink-0" style={{ background: "#3FAE35" }} />
                  <span className="text-sm font-bold" style={{ color: "#063B63" }}>{f}</span>
                </div>
              ))}
            </div>
          </div>
          <div className="rounded-3xl overflow-hidden biosyn-card !p-0">
            <ImageWithFallback src={IMAGES.lab} alt="Laboratory technician performing aquaculture water testing" className="w-full h-[380px] object-cover" />
          </div>
        </div>
      </section>

      {/* TESTING SERVICES */}
      <section id="services" className="py-16 md:py-24 grain" style={{ background: "#FFFFFF" }}>
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center max-w-3xl mx-auto">
            <p className="overline-label mb-3">Testing Services</p>
            <h2 className="font-heading text-3xl sm:text-4xl font-extrabold tracking-tight" style={{ color: "#063B63" }}>
              Complete Aquaculture Diagnostic Coverage
            </h2>
          </div>
          <div className="mt-12 grid sm:grid-cols-2 lg:grid-cols-4 gap-5">
            {SERVICES.map((s, i) => (
              <div key={s.title} data-testid={`lab-service-${i}`} className="biosyn-card p-6 flex flex-col">
                <div className="w-12 h-12 rounded-xl flex items-center justify-center mb-4" style={{ background: "rgba(22,184,212,0.12)" }}>
                  <s.icon className="w-6 h-6" style={{ color: "#087FBF" }} />
                </div>
                <h3 className="font-heading font-bold text-base leading-snug uppercase tracking-wide" style={{ color: "#063B63" }}>{s.title}</h3>
                <p className="mt-2.5 text-sm leading-relaxed flex-1" style={{ color: "#4A6B82" }}>{s.items}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* OUR APPROACH */}
      <section id="approach" className="py-16 md:py-24" style={{ background: "#063B63" }}>
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <p className="overline-label mb-3" style={{ color: "#16B8D4" }}>Our Approach</p>
          <h2 className="font-heading text-3xl sm:text-4xl font-extrabold tracking-tight text-white">Data-Driven Aquaculture Management</h2>
          <p className="mt-5 max-w-3xl mx-auto text-base leading-relaxed" style={{ color: "rgba(255,255,255,0.82)" }}>
            Regular laboratory testing can help farmers monitor water quality, pond-bottom conditions, mineral balance,
            plankton populations, microbial activity and disease-related indicators throughout the culture cycle.
          </p>
          <div className="mt-12 grid grid-cols-2 md:grid-cols-4 gap-4 max-w-4xl mx-auto">
            {PROCESS.map((p, i) => (
              <div key={p.label} className="relative">
                <div className="rounded-2xl p-6 flex flex-col items-center" style={{ background: "rgba(255,255,255,0.07)", border: "1px solid rgba(22,184,212,0.3)" }}>
                  <span className="w-12 h-12 rounded-full flex items-center justify-center text-white mb-3" style={{ background: i === PROCESS.length - 1 ? "#3FAE35" : "#087FBF" }}>
                    <p.icon className="w-6 h-6" />
                  </span>
                  <span className="font-heading font-extrabold tracking-widest text-white">{p.label}</span>
                </div>
                {i < PROCESS.length - 1 && (
                  <ChevronDown className="hidden md:block absolute top-1/2 -right-3 -translate-y-1/2 -rotate-90 w-5 h-5" style={{ color: "#16B8D4" }} />
                )}
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* WHY */}
      <section id="why" className="py-16 md:py-24">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center max-w-3xl mx-auto">
            <p className="overline-label mb-3">Why BIOSYN Aqua Lab</p>
            <h2 className="font-heading text-3xl sm:text-4xl font-extrabold tracking-tight" style={{ color: "#063B63" }}>
              Precision You Can Trust
            </h2>
          </div>
          <div className="mt-12 grid sm:grid-cols-2 lg:grid-cols-4 gap-5">
            {WHY.map((w) => (
              <div key={w.title} className="biosyn-card p-7">
                <div className="w-12 h-12 rounded-xl flex items-center justify-center mb-4" style={{ background: "rgba(63,174,53,0.12)" }}>
                  <w.icon className="w-6 h-6" style={{ color: "#197A35" }} />
                </div>
                <h3 className="font-heading font-bold text-base uppercase tracking-wide" style={{ color: "#063B63" }}>{w.title}</h3>
                <p className="mt-2 text-sm leading-relaxed" style={{ color: "#4A6B82" }}>{w.desc}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* CAREERS */}
      <section id="careers" className="py-16 md:py-24 grain" style={{ background: "#FFFFFF" }}>
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="biosyn-card !p-8 md:!p-14 text-center relative overflow-hidden">
            <p className="overline-label mb-3">Careers</p>
            <h2 className="font-heading text-3xl sm:text-4xl font-extrabold tracking-tight" style={{ color: "#063B63" }}>
              We Are Hiring – Across India
            </h2>
            <p className="mt-4 max-w-2xl mx-auto text-base leading-relaxed" style={{ color: "#4A6B82" }}>
              Join BIOSYN and be part of the future of the Aquaculture Industry. We are looking for passionate and talented
              professionals to join our growing team across India.
            </p>
            <p className="mt-6 text-sm font-semibold" style={{ color: "#063B63" }}>Opportunities in:</p>
            <div className="mt-3 flex flex-wrap justify-center gap-2.5">
              {["Technical", "Sales & Marketing", "Business Development", "Laboratory", "Production", "Operations"].map((r) => (
                <span key={r} className="px-4 py-2 rounded-full text-sm font-semibold" style={{ background: "#F5FAFC", color: "#087FBF", border: "1px solid rgba(8,127,191,0.15)" }}>
                  {r}
                </span>
              ))}
            </div>
            <a href={RESUME_MAILTO} data-testid="lab-send-resume" className="btn-green mt-8">
              <Send className="w-4 h-4" /> Send Your Resume
            </a>
          </div>
        </div>
      </section>

      {/* CONTACT */}
      <section id="contact" className="py-16 md:py-24">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center max-w-3xl mx-auto">
            <p className="overline-label mb-3">Contact</p>
            <h2 className="font-heading text-3xl sm:text-4xl font-extrabold tracking-tight" style={{ color: "#063B63" }}>
              Contact BIOSYN Aqua Lab
            </h2>
            <p className="mt-4 text-base leading-relaxed" style={{ color: "#4A6B82" }}>
              For aquaculture testing, diagnostic services and technical information, please contact our team.
            </p>
          </div>
          <div className="mt-10 grid sm:grid-cols-2 lg:grid-cols-3 gap-5 max-w-4xl mx-auto">
            <a href={`tel:${LAB_PHONE}`} data-testid="lab-call" className="biosyn-card p-6 flex flex-col items-center text-center gap-3">
              <span className="w-12 h-12 rounded-full flex items-center justify-center text-white" style={{ background: "#087FBF" }}><Phone className="w-6 h-6" /></span>
              <span className="font-heading font-bold" style={{ color: "#063B63" }}>Call Now</span>
              <span className="text-sm" style={{ color: "#4A6B82" }}>89859 30908</span>
            </a>
            <a href={`mailto:${LAB_EMAIL}`} data-testid="lab-email" className="biosyn-card p-6 flex flex-col items-center text-center gap-3">
              <span className="w-12 h-12 rounded-full flex items-center justify-center text-white" style={{ background: "#16B8D4" }}><Mail className="w-6 h-6" /></span>
              <span className="font-heading font-bold" style={{ color: "#063B63" }}>Email Us</span>
              <span className="text-sm break-all" style={{ color: "#4A6B82" }}>{LAB_EMAIL}</span>
            </a>
            <a href={`https://wa.me/${LAB_PHONE_INTL}?text=${encodeURIComponent(WA_MESSAGE)}`} target="_blank" rel="noopener noreferrer" data-testid="lab-whatsapp" className="biosyn-card p-6 flex flex-col items-center text-center gap-3 sm:col-span-2 lg:col-span-1">
              <span className="w-12 h-12 rounded-full flex items-center justify-center text-white" style={{ background: "#25D366" }}><MessageCircle className="w-6 h-6" /></span>
              <span className="font-heading font-bold" style={{ color: "#063B63" }}>WhatsApp</span>
              <span className="text-sm" style={{ color: "#4A6B82" }}>Chat with our team</span>
            </a>
          </div>

          {/* Registered address */}
          <div className="mt-12 max-w-2xl mx-auto biosyn-card p-8 text-center">
            <MapPin className="w-8 h-8 mx-auto mb-3" style={{ color: "#3FAE35" }} />
            <p className="overline-label mb-2">Registered Address</p>
            <p className="text-sm leading-relaxed" style={{ color: "#4A6B82" }}>
              H.No. 20-79/301,<br />Surya Prabha Nilayam,<br />Dayanand Nagar,<br />Secunderabad,<br />Medchal-Malkajgiri,<br />Telangana - 500044<br />India
            </p>
            <p className="mt-4 text-sm font-semibold" style={{ color: "#063B63" }}>GST No: 36B00PP1044QIZA</p>
          </div>
        </div>
      </section>

      {/* FOOTER */}
      <footer data-testid="lab-footer" style={{ background: "#063B63", color: "rgba(255,255,255,0.8)" }}>
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-14">
          <div className="flex flex-col md:flex-row gap-10 md:gap-16">
            <div className="md:max-w-sm">
              <div className="inline-block bg-white rounded-xl px-3 py-2">
                <img src={BRAND.logo} alt="BIOSYN Aqua Lab" className="h-11" />
              </div>
              <p className="mt-4 font-heading font-bold text-white">BIOSYN Aqua Lab</p>
              <p className="text-sm" style={{ color: "#16B8D4" }}>Advanced Aquaculture Testing &amp; Diagnostic Laboratory</p>
              <p className="mt-4 text-sm leading-relaxed">Water • Soil • Plankton • Microbiology • Pathogens • Shrimp &amp; Fish Health</p>
            </div>
            <div>
              <p className="font-heading font-bold text-white mb-4">Contact</p>
              <ul className="space-y-3 text-sm">
                <li className="flex items-center gap-2.5"><Phone className="w-4 h-4" style={{ color: "#16B8D4" }} /><a href={`tel:${LAB_PHONE}`} className="hover:text-white">89859 30908</a></li>
                <li className="flex items-center gap-2.5"><Mail className="w-4 h-4" style={{ color: "#16B8D4" }} /><a href={`mailto:${LAB_EMAIL}`} className="hover:text-white break-all">{LAB_EMAIL}</a></li>
                <li className="flex gap-2.5"><MapPin className="w-4 h-4 mt-0.5 shrink-0" style={{ color: "#16B8D4" }} />
                  <span>H.No. 20-79/301, Surya Prabha Nilayam,<br />Dayanand Nagar, Secunderabad,<br />Medchal-Malkajgiri, Telangana - 500044, India</span>
                </li>
                <li className="text-sm">GST No: 36B00PP1044QIZA</li>
              </ul>
            </div>
          </div>
          <div className="mt-10 pt-6 border-t flex flex-col md:flex-row gap-4 items-center justify-between text-xs" style={{ borderColor: "rgba(255,255,255,0.12)" }}>
            <p className="font-semibold tracking-wide" style={{ color: "#3FAE35" }}>FOR AQUACULTURE USE ONLY • NOT FOR HUMAN USE</p>
            <p>© 2026 BIOSYN. All Rights Reserved.</p>
          </div>
        </div>
      </footer>
    </div>
  );
}
